import { AdminRole } from "@repo/api-dtos"
import { GitCompareArrows, ListChecks, Rocket, Search } from "lucide-react"
import { getNavigation, NAV_GROUPS, navItemTitle } from "@/config/navigation"
import type { PaletteCommand } from "../types"
import { gamePickerStep } from "./game-picker"

export const NAVIGATION_TARGETS = [
	"/catalogue-v2/proposals",
	"/catalogue/games/$gameSlug",
	"/catalogue-v2/$gameSlug",
	"/diff",
] as const

export function buildNavigationCommands(): PaletteCommand[] {
	const navCommands = getNavigation().map((item): PaletteCommand => {
		const title =
			item.groupLabel === item.pageTitle ? item.title : navItemTitle(item)

		return {
			id: `nav:${item.href}`,
			label:
				item.group === NAV_GROUPS.overview
					? title
					: `${item.groupLabel} › ${title}`,
			description: item.description,
			icon: item.icon,
			group: "navigate",
			requiredRoles: item.requiredRoles,
			keywords: [item.href],
			perform: (ctx) => {
				ctx.navigate({ to: item.href })
				ctx.close()
			},
		}
	})

	const hiddenPages: PaletteCommand[] = [
		{
			id: "nav:/catalogue-v2/proposals",
			label: "Catalogue › Change propositions",
			description: "Pending catalogue-review proposals",
			icon: GitCompareArrows,
			group: "navigate",
			requiredRoles: [AdminRole.CatalogueRead],
			perform: (ctx) => {
				ctx.navigate({ to: "/catalogue-v2/proposals" })
				ctx.close()
			},
		},
		{
			id: "nav:game-backlog",
			label: "Catalogue › Game backlog…",
			description: "Per-game expansion backlog and price coverage",
			icon: Search,
			group: "navigate",
			requiredRoles: [AdminRole.CatalogueRead],
			submenu: (ctx) =>
				gamePickerStep(ctx, "Game backlog", (game) => ({
					perform: (c) => {
						c.navigate({
							to: "/catalogue/games/$gameSlug",
							params: { gameSlug: game.gameSlug },
						})
						c.close()
					},
				})),
			perform: async (ctx) => {
				ctx.push(
					await gamePickerStep(ctx, "Game backlog", (game) => ({
						perform: (c) => {
							c.navigate({
								to: "/catalogue/games/$gameSlug",
								params: { gameSlug: game.gameSlug },
							})
							c.close()
						},
					})),
				)
			},
		},
		{
			id: "nav:review-game",
			label: "Catalogue › Review game…",
			description: "Catalogue-review workspace for one game",
			icon: ListChecks,
			group: "navigate",
			requiredRoles: [AdminRole.CatalogueRead],
			perform: async (ctx) => {
				ctx.push(
					await gamePickerStep(ctx, "Review game", (game) => ({
						perform: (c) => {
							c.navigate({
								to: "/catalogue-v2/$gameSlug",
								params: { gameSlug: game.gameSlug },
							})
							c.close()
						},
					})),
				)
			},
		},
		{
			id: "nav:manage-diff-game",
			label: "Catalogue › Manage diff for game…",
			description: "Open the diff manager focused on one game",
			icon: Rocket,
			group: "navigate",
			requiredRoles: [AdminRole.CatalogueRead],
			perform: async (ctx) => {
				ctx.push(
					await gamePickerStep(ctx, "Manage diff", (game) => ({
						perform: (c) => {
							c.navigate({ to: "/diff", search: { game: game.gameSlug } })
							c.close()
						},
					})),
				)
			},
		},
	]

	return [...navCommands, ...hiddenPages]
}
